import { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Image } from 'primereact/image';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { StyleService } from '../../service/StyleService';
import { StyleImageService } from '../../service/StyleImageService';
import Style from '../../models/Style';
import { useParams } from 'react-router-dom';
import StyleSetup from './StyleSetup';
import StyleStripeView from './StyleStripe';
import StyleMinutes from './StyleMinutes';
import StyleCostElements from './StyleCostElements';
import StyleLettering from './StyleLettering';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import StyleBillOfMaterials from './StyleBillOfMaterials';
import StyleImage from '../../models/StyleImage';
import StyleQuantityPricing from './StyleQuantityPricing';
import StyleRules from './StyleRules';
import StyleItemSpecs from './StyleItemSpecs';
import StyleDrawing from './StyleDrawing';
import StyleExcept from './StyleExcept';
import { usePermissions } from '../../PermissionsContext';
import { roleBasedMenuAccess } from '../../authConfig';

const StyleView = () => {
    let emptyStyle: Style = {
        styleCode: '',
        description: '',
        company: 0,
        garmentType: '',
        itemClass: '',
        materialGroup: '',
        materialGroupDescription: '',
        materialGroupDescription2: '',
        productCategory: '',
        productAllocation: 0,
        effectiveDate: '',
        hasBom: false,
        hasStripe: false,
        rowNumber: 0,
        productHierarchyOne: '',
        productHierarchyTwo: '',
        productHierarchyThree: '',
        uom: '',
        lastUpdateUserSap: '',
        lastUpdateSap: '',
        styleEffDate: '',
        styleMaterialGroup: '',
        styleMaterialType: '',
        styleStatus: '',
        styleLongDesc: '',
        styleCatCenYr: 0,
        styleCatPage: '',
        styleCatPict: '',
        styleCatBrand: '',
        styleWeight: 0,
        styleCompanyId: 0,
        styleLetprccd: ''
    };

    const [style, setStyle] = useState<Style>(emptyStyle);
    const [styleImageFront, setStyleImageFront] = useState<BlobPart>();
    const [styleImageBack, setStyleImageBack] = useState<BlobPart>();
    const [activeStyleTabIndex, setActiveStyleTabIndex] = useState<number>(0);

    const toast = useRef<any>(null);
    const cardStyleBillOfMaterials = useRef<any>(null);
    const cardStyleBillOfMaterialsNotFound = useRef<any>(null);
    const { styleCode } = useParams();
    const { roles } = usePermissions();

    useEffect(() => {
        if (!styleCode) { return; }

        StyleService.getStyle(styleCode).then(data => {
            if (data.styleCode !== '') {
                setStyle(data);

                StyleImageService.getStyleImages(styleCode).then((s: StyleImage[]) => {
                    if (s.length > 0) {
                        setStyleImageFront(s[0].image_Image);
                        setStyleImageBack(s[1].image_Image);
                    }
                });
            } else {
                cardStyleBillOfMaterials.current?.classList.remove('block');
                cardStyleBillOfMaterials.current?.classList.add('hidden');
                cardStyleBillOfMaterialsNotFound.current?.classList.remove('hidden');
                cardStyleBillOfMaterialsNotFound.current?.classList.add('block');
            }
        }).catch(err => {
            cardStyleBillOfMaterials.current?.classList.remove('block');
            cardStyleBillOfMaterials.current?.classList.add('hidden');
            cardStyleBillOfMaterialsNotFound.current?.classList.remove('hidden');
            cardStyleBillOfMaterialsNotFound.current?.classList.add('block');
        });
    }, [styleCode]);

    const noStyleImageFound = { height: 103, color: 'red' };

    const styleTitle = (
        <>
            <div className="flex flex-wrap">
                <div className="flex align-items-center">
                    <label className="font-bold text-lg">Style:</label>
                    <label className="ml-2 text-primary font-bold text-lg">{`${style.styleCode} (${style.description})`}</label>
                </div>
            </div>
        </>
    );

    const styleSubTitle = (
        <>
            <div className="flex flex-wrap">
                <div className="flex align-items-center">
                    <label className="ml-2 font-bold">Material Group:</label>
                    <label className="ml-2 text-primary font-bold">{style.materialGroup}</label>
                </div>
                <div className="flex align-items-center">
                    <label className="ml-2 font-bold">Description:</label>
                    <label className="ml-2 text-primary font-bold">{style.materialGroupDescription}</label>
                </div>
                <div className="flex align-items-center">
                    <label className="ml-2 font-bold">Description 2:</label>
                    <label className="ml-2 text-primary font-bold">{style.materialGroupDescription2}</label>
                </div>
            </div>

            <Divider />
        </>
    );

    const getVisibleTabs = () => {
        if (roles.includes("Admin")) {
            return [
                "Bill of Material",
                "Style Setup",
                "Drawing",
                "Style Rules",
                "Stripe Details",
                "Minutes",
                "Cost Elements",
                "Lettering",
                "Quantity Pricing",
                "Detailing Exceptions",
                "Item Specs"
            ];
        }

        const visibleTabs: string[] = [];

        roles.forEach((role) => {
            const rolePermissions = roleBasedMenuAccess[role];
            if (rolePermissions && rolePermissions.tabs.Style) {
                visibleTabs.push(...rolePermissions.tabs.Style);
            }
        });

        return Array.from(new Set(visibleTabs));
    };

    const visibleTabs = getVisibleTabs();

    const onSetStyleImageFront = (frontImage: BlobPart | undefined) => {
        setStyleImageFront(frontImage);
    }

    const onSetStyleImageBack = (backImage: BlobPart | undefined) => {
        setStyleImageBack(backImage);
    }

    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />

                <Card ref={cardStyleBillOfMaterials} className="layout-main-card border-noround" title={styleTitle} subTitle={styleSubTitle} style={{ minHeight: 'calc(100vh - 5rem)' }}>
                    <div className="bom-subtable">
                        <div className="grid">
                            <div className="col-fixed">
                                <Card title="Front">
                                    {
                                        styleImageFront === undefined
                                            ? <p style={noStyleImageFound}>No Front Image Found.</p>
                                            : <Image className="styleImageFront" src={`data:image/png;base64,${styleImageFront}`} alt="Image" width="150" />
                                    }
                                </Card>
                                <Card title="Back" className="mt-3">
                                    {
                                        styleImageBack === undefined
                                            ? <p style={noStyleImageFound}>No Back Image Found.</p>
                                            : <Image className="styleImageBack" src={`data:image/png;base64,${styleImageBack}`} alt="No image found." width="150" />
                                    }
                                </Card>
                            </div>
                            <div className="col">
                                <TabView activeIndex={activeStyleTabIndex} onTabChange={(e) => setActiveStyleTabIndex(e.index)}>
                                    {visibleTabs.includes("Bill of Material") && (
                                        <TabPanel header="Bill of Materials">
                                            <StyleBillOfMaterials />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Style Setup") && (
                                        <TabPanel header="Style Setup">
                                            <StyleSetup />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Drawing") && (
                                        <TabPanel header="Drawing">
                                            <StyleDrawing frontImage={styleImageFront} backImage={styleImageBack} onSetStyleImageFront={onSetStyleImageFront} onSetStyleImageBack={onSetStyleImageBack} />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Style Rules") && (
                                        <TabPanel header="Style Rules">
                                            <StyleRules />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Stripe Details") && (
                                        <TabPanel header="Stripe Details">
                                            <StyleStripeView />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Minutes") && (
                                        <TabPanel header="Minutes">
                                            <StyleMinutes />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Cost Elements") && (
                                        <TabPanel header="Cost Elements">
                                            <StyleCostElements />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Lettering") && (
                                        <TabPanel header="Lettering">
                                            <StyleLettering />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Quantity Pricing") && (
                                        <TabPanel header="Quantity Pricing">
                                            <StyleQuantityPricing />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Detailing Exceptions") && (
                                        <TabPanel header="Detailing Exceptions">
                                            <StyleExcept />
                                        </TabPanel>
                                    )}
                                    {visibleTabs.includes("Item Specs") && (
                                        <TabPanel header="Item Specs">
                                            <StyleItemSpecs />
                                        </TabPanel>
                                    )}
                                </TabView>

                            </div>
                        </div>
                    </div>
                </Card>

                <Card ref={cardStyleBillOfMaterialsNotFound} className="layout-main-card border-noround hidden">
                    <Message severity="error" text="This style should be setup first in SAP" />
                </Card>
            </div>
        </div>
    );
}

export default StyleView;